import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { LiveToolbar } from "@faire/prototype-playground/platform/components/toolbar/LiveToolbar";
import type { PrototypeMetadata } from "@faire/prototype-playground/workspace/types";
import { Button } from "@faire/slate/components/button";

import { CompassLayout } from "../../../components/compass/CompassLayout";
import RetailerLayout from "../../../components/RetailerLayout";
import {
  ProductTileATC,
  ProductTileATCCompact,
  SWATCH_SETS,
  type ProgramTagChip,
  type ProgramTagChipKind,
  type TileProduct,
} from "../../../components/shared/ProductTilesSearchTile";
import { fallAccessoriesSearchProducts } from "../../../data/entity_sets/fallAccessoriesSearchProducts";
import { toDisplayProduct } from "../../../view_models/products";

import { AllFiltersModal } from "../filters/components/AllFiltersModal";
import { QuickFilterBar } from "./QuickFilterBar";

import { filterReorderedProducts, MOCK_REORDERED_PRODUCTS } from "./mockProducts";
import { RelatedProductsCarousel } from "./RelatedProductsCarousel";
import { RelatedSearches } from "./RelatedSearches";
import type { RelatedSearch } from "./searchModel";
import {
  hashString,
  productsForQuery,
  rationaleBulletsForProduct,
  refinementFiltersForQuery,
  steerProducts,
  suggestionsForQuery,
  type ResultSteer,
} from "./searchModel";
import { resultsGridStyle } from "./resultsGridStyles";
import { ResultsGridSkeleton } from "./ResultsGridSkeleton";
import { ResultSteerRow } from "./ResultSteerRow";
import { SectionHeading } from "./SectionHeading";
import { TypeaheadSearchBar } from "./TypeaheadSearchBar";

export const prototypeMeta: PrototypeMetadata = {
  name: "Search Vision 2026",
  owner: "Sam Chieng",
  email: "sam.chieng@faire.com",
  status: "in-progress",
  platform: "web",
  createdAt: "2026-07-29",
  lastUpdated: "2026-07-29",
  surfaceArea: "Retailer",
  category: "experimental",
  creationTool: "claude-code",
  thumbnail: "/images/thumbnails/sc-next-gen-search.png",
  description:
    "Next generation search experience tying together explorations across product tiles, refinement, find similar, and typeahead.",
};

const SEARCH_QUERY = "fall accessories";

/** Where every tile's "Find similar" control routes to. */
const FIND_SIMILAR_PATH = "/sc/find-similar";

const DISCOUNTS = ["Up to 25% off", "20% off", "15% off"];

const SHIPPING_PROGRAM_LABELS = ["Free shipping", "Free shipping over $250"];

const DISCOUNT_PROGRAM_LABELS = ["10% off", "15% off", "20% off", "5% off"];

const SOURCE_PROGRAM_LABELS = ["Faire Source"];

const FREE_SHIPPING_LABELS = ["Free shipping", "Free shipping over $250"];

const PRODUCT_METADATA_OPTIONS = ["2 sizes", "Preorder"];

/** Faire Source chips sort ahead of shipping, which sorts ahead of discount. */
const PROGRAM_TAG_KIND_ORDER: Record<ProgramTagChipKind, number> = {
  3: 0,
  1: 1,
  2: 2,
};

/** Seeded queries the typeahead completes against as the viewer types. */
const SUGGESTION_POOL = [
  "fall accessories",
  "fall accessories womens",
  "wool scarf",
  "cashmere scarf",
  "knit beanie",
  "knit poncho",
  "wool shawl",
  "silk scarf",
  "plaid scarf",
  "alpaca throw",
  "wool socks",
];

const MAX_SUGGESTIONS = 7;

const MAX_RECENT_QUERIES = 3;

const INITIAL_RECENT_QUERIES = ["cashmere scarf", "knit beanie", "wool shawl"];

/** 6 tiles per row at xl, so each band is a whole number of rows. */
const TILES_PER_ROW = 6;
const TOP_PICKS_COUNT = 5;
/** 2 + 4 + 4 rows = 60 tiles across "All results" on first paint. */
const FIRST_BAND_ROWS = 2;
const SECOND_BAND_ROWS = 4;
const THIRD_BAND_ROWS = 4;
const CAROUSEL_SIZE = 8;

/**
 * One tranche of appended results, plus the steer that produced it. The feed
 * interleaves these with `ResultSteerRow`, so a batch's own steer is also what
 * the row above it renders as chosen.
 */
interface ResultBatch {
  items: TileProduct[];
  /** `undefined` when the batch came from the CTA or a plain scroll. */
  steer: ResultSteer | undefined;
}

/**
 * "Show more results" batches. The catalogue is finite, so batches loop back
 * over `visibleProducts` (modulo) rather than ever running out.
 */
const EXTRA_RESULTS_PAGE_SIZE = TILES_PER_ROW * 5;

/** Two rows of placeholders — enough to read as loading without a wall of grey. */
const SKELETON_TILE_COUNT = TILES_PER_ROW * 2;

/**
 * Split cadence, keyed to how the batch was asked for. A deliberate press — the
 * CTA or a steer option — answers immediately, because the viewer is waiting on
 * it. Scrolling past the row is passive continuation, so it takes its time and
 * reads as the feed working rather than as a response.
 */
const PRESS_BATCH_DELAY_MS = 500;
const SCROLL_BATCH_DELAY_MS = 2500;

const pickRandom = <T,>(arr: T[], count: number): Set<number> => {
  const indices = Array.from({ length: arr.length }, (_, index) => index);
  for (let i = indices.length - 1; i > 0; i--) {
    const swapWith = Math.floor(Math.random() * (i + 1));
    [indices[i], indices[swapWith]] = [indices[swapWith], indices[i]];
  }
  return new Set(indices.slice(0, count));
};

const labelForChipKind = (kind: ProgramTagChipKind, salt: number): string => {
  if (kind === 1) {
    return SHIPPING_PROGRAM_LABELS[salt % SHIPPING_PROGRAM_LABELS.length];
  }
  if (kind === 2) {
    return DISCOUNT_PROGRAM_LABELS[salt % DISCOUNT_PROGRAM_LABELS.length];
  }
  return SOURCE_PROGRAM_LABELS[salt % SOURCE_PROGRAM_LABELS.length];
};

const sortProgramTagChipsByKind = (chips: ProgramTagChip[]): ProgramTagChip[] =>
  [...chips].sort(
    (first, second) =>
      PROGRAM_TAG_KIND_ORDER[first.chipKind] -
      PROGRAM_TAG_KIND_ORDER[second.chipKind]
  );

const buildProgramTagChips = (
  hash: number,
  include: boolean
): ProgramTagChip[] => {
  if (!include) {
    return [];
  }
  const preferTwo = (hash >>> 3) % 3 !== 0;
  const firstKind = ((hash % 3) + 1) as ProgramTagChipKind;
  const secondKind = (((hash >>> 5) % 3) + 1) as ProgramTagChipKind;
  const mixed = (hash >>> 7) % 2 === 0;
  const kindB =
    mixed && secondKind === firstKind
      ? (((((firstKind % 3) + 1) % 3) + 1) as ProgramTagChipKind)
      : secondKind;

  const chipA: ProgramTagChip = {
    label: labelForChipKind(firstKind, hash),
    chipKind: firstKind,
  };
  if (!preferTwo) {
    return sortProgramTagChipsByKind([chipA]);
  }
  const chipB: ProgramTagChip = {
    label: labelForChipKind(kindB, hash >>> 4),
    chipKind: kindB,
  };
  if (chipA.label === chipB.label && chipA.chipKind === chipB.chipKind) {
    return sortProgramTagChipsByKind([chipA]);
  }
  return sortProgramTagChipsByKind([chipA, chipB]);
};

/**
 * Top picks and the three result bands are disjoint slices of this pool, so it
 * has to cover both: 5 + 60. The real catalogue is shorter than that, so the
 * tail repeats rather than the last band coming up short.
 */
const SOURCE_PRODUCT_COUNT =
  TOP_PICKS_COUNT +
  (FIRST_BAND_ROWS + SECOND_BAND_ROWS + THIRD_BAND_ROWS) * TILES_PER_ROW;

const REAL_PRODUCTS = fallAccessoriesSearchProducts.map(toDisplayProduct);
const BASE_PRODUCTS = REAL_PRODUCTS.slice(0, SOURCE_PRODUCT_COUNT);
const SOURCE_PRODUCTS =
  BASE_PRODUCTS.length >= SOURCE_PRODUCT_COUNT
    ? BASE_PRODUCTS
    : Array.from(
        { length: SOURCE_PRODUCT_COUNT },
        (_, index) => BASE_PRODUCTS[index % BASE_PRODUCTS.length]
      );

const SALE_INDICES = pickRandom(SOURCE_PRODUCTS, 4);
const SWATCH_INDICES = pickRandom(SOURCE_PRODUCTS, 12);
const PROGRAM_TAG_INDICES = pickRandom(SOURCE_PRODUCTS, 48);

let freeShippingLabelCounter = 0;

const products: TileProduct[] = SOURCE_PRODUCTS.map((product, index) => {
  const hasSale = SALE_INDICES.has(index);
  const hasSwatches = SWATCH_INDICES.has(index);
  const swatchSet = SWATCH_SETS[index % SWATCH_SETS.length];
  const hash = hashString(product.token);
  const programTagChips = buildProgramTagChips(
    hash,
    PROGRAM_TAG_INDICES.has(index)
  );
  const minDollars = Number(product.minOrder?.match(/\$(\d+)/)?.[1] ?? 100);
  const priceDollars = product.priceDollars > 0 ? product.priceDollars : 0.01;

  const freeShippingLabel = (product.freeShipping ?? false)
    ? FREE_SHIPPING_LABELS[
        freeShippingLabelCounter++ % FREE_SHIPPING_LABELS.length
      ]
    : undefined;
  const productMetadata =
    hash % 4 === 0
      ? PRODUCT_METADATA_OPTIONS[(hash >>> 8) % PRODUCT_METADATA_OPTIONS.length]
      : undefined;

  return {
    id: `${product.token}-${index}`,
    imageUrl: product.imageUrl,
    brandName: product.brandName,
    brandAvatarUrl: product.brandAvatarUrl,
    productName: product.name,
    price: product.priceDollars,
    msrp: product.msrpDollars ?? product.priceDollars * 1.5,
    minOrder: `$${minDollars} min`,
    freeShipping: product.freeShipping ?? false,
    freeShippingLabel,
    isBestseller: product.isTopShop ?? index % 4 === 0,
    rating: product.rating ?? 3.8 + (index % 12) * 0.1,
    swatchColors: hasSwatches ? swatchSet.slice(0, 5) : [],
    extraSwatches: hasSwatches ? Math.max(0, 11 - 5) : 0,
    attributes: [],
    programTagChips,
    searchMatchTagLabels: [],
    discount: hasSale ? DISCOUNTS[index % DISCOUNTS.length] : undefined,
    minQty: Math.ceil(minDollars / priceDollars),
    productMetadata,
  };
});

const FIRST_BAND_END = TOP_PICKS_COUNT + FIRST_BAND_ROWS * TILES_PER_ROW;
const SECOND_BAND_END = FIRST_BAND_END + SECOND_BAND_ROWS * TILES_PER_ROW;
const THIRD_BAND_END = SECOND_BAND_END + THIRD_BAND_ROWS * TILES_PER_ROW;

// The rails are anchored to the tail of the catalogue. The three bands consume
// most of the 60 products, so a few here also appear in a band — acceptable for
// rails that are explicitly a different context ("your brands", "your area").
const FIRST_CAROUSEL = SOURCE_PRODUCTS.slice(
  -CAROUSEL_SIZE * 2,
  -CAROUSEL_SIZE
);
const SECOND_CAROUSEL = SOURCE_PRODUCTS.slice(-CAROUSEL_SIZE);

const RELATED_SEARCHES: RelatedSearch[] = [
  {
    id: "scarves",
    label: "Scarves",
    imageUrl: SOURCE_PRODUCTS[0].imageUrl,
    query: "wool scarf",
  },
  {
    id: "cashmere",
    label: "Cashmere",
    imageUrl: SOURCE_PRODUCTS[4].imageUrl,
    query: "cashmere scarf",
  },
  {
    id: "beanies",
    label: "Beanies",
    imageUrl: SOURCE_PRODUCTS[9].imageUrl,
    query: "knit beanie",
  },
  {
    id: "ponchos-shawls",
    label: "Ponchos & shawls",
    imageUrl: SOURCE_PRODUCTS[5].imageUrl,
    query: "knit poncho",
  },
  {
    id: "plaid-check",
    label: "Plaid & check",
    imageUrl: SOURCE_PRODUCTS[2].imageUrl,
    query: "plaid scarf",
  },
];

export default function NextGenSearchPage() {
  const history = useHistory();
  const [carouselTilesPerView, setCarouselTilesPerView] = useState(8);
  const [batches, setBatches] = useState<ResultBatch[]>([]);
  const [isLoadingMoreResults, setIsLoadingMoreResults] = useState(false);
  /** The last steer row — scrolling past it loads the next batch. */
  const liveSteerRowRef = useRef<HTMLDivElement>(null);

  // Filter chips are presentation only on this page — they never touch the
  // grid. Committing a search is the only thing that moves results.
  const [appliedFilterKeys, setAppliedFilterKeys] = useState<Set<string>>(
    new Set()
  );
  const [isAllFiltersOpen, setIsAllFiltersOpen] = useState(false);
  const [filterRadioSelections, setFilterRadioSelections] = useState<
    Record<string, string>
  >({});
  const [filterCheckedValues, setFilterCheckedValues] = useState<Set<string>>(
    new Set()
  );

  /** The committed search driving the results, vs. what's typed in the input. */
  const [activeQuery, setActiveQuery] = useState(SEARCH_QUERY);
  const [typeaheadQuery, setTypeaheadQuery] = useState(SEARCH_QUERY);
  const [isTypeaheadOpen, setIsTypeaheadOpen] = useState(false);
  const [recentQueries, setRecentQueries] = useState(INITIAL_RECENT_QUERIES);
  const now = useMemo(() => new Date(), []);
  const reorderedMatches = useMemo(
    () => filterReorderedProducts(typeaheadQuery, MOCK_REORDERED_PRODUCTS),
    [typeaheadQuery]
  );

  const visibleProducts = productsForQuery(products, activeQuery);
  const queryRefinementFilters = refinementFiltersForQuery(activeQuery);

  const topPicks = visibleProducts.slice(0, TOP_PICKS_COUNT);
  const firstBand = visibleProducts.slice(TOP_PICKS_COUNT, FIRST_BAND_END);
  const secondBand = visibleProducts.slice(FIRST_BAND_END, SECOND_BAND_END);
  const thirdBand = visibleProducts.slice(SECOND_BAND_END, THIRD_BAND_END);

  const suggestionRows = suggestionsForQuery(
    typeaheadQuery,
    SUGGESTION_POOL,
    MAX_SUGGESTIONS
  ).map((text) => ({ text }));

  /**
   * Appends one batch of results. `steer` is passed explicitly rather than read
   * from state so a steer press can choose and load in the same tick.
   *
   * A steered batch starts at the top of its own ordering, so
   * "Budget-friendly" opens on the cheapest items. An unsteered batch instead
   * walks forward by page, so consecutive scroll loads don't re-show the same
   * tiles. Either way the modulo wraps, letting a finite catalogue keep
   * yielding batches.
   *
   * `viaPress` distinguishes a deliberate request from passive scrolling, which
   * is what sets the delay — see the two delay constants.
   */
  const loadBatch = useCallback(
    (steer: ResultSteer | undefined, viaPress = false) => {
      if (isLoadingMoreResults || visibleProducts.length === 0) {
        return;
      }
      const delayMs = viaPress
        ? PRESS_BATCH_DELAY_MS
        : SCROLL_BATCH_DELAY_MS;
      setIsLoadingMoreResults(true);
      window.setTimeout(() => {
        setBatches((previous) => {
          const page = previous.length;
          const steered = steerProducts(visibleProducts, steer, page);
          const start =
            steer === undefined
              ? (page * EXTRA_RESULTS_PAGE_SIZE) % steered.length
              : 0;
          const items: TileProduct[] = [];
          for (let index = 0; index < EXTRA_RESULTS_PAGE_SIZE; index += 1) {
            items.push(steered[(start + index) % steered.length]);
          }
          return [...previous, { items, steer }];
        });
        setIsLoadingMoreResults(false);
      }, delayMs);
    },
    [isLoadingMoreResults, visibleProducts]
  );

  // A new committed search restarts the feed instead of appending onto the
  // previous query's loaded batches.
  useEffect(() => {
    setBatches([]);
  }, [activeQuery]);

  /**
   * Scrolling *past* the live steer row loads the next batch unsteered — the row
   * is a shortcut, not a gate.
   *
   * The row itself is observed rather than a sentinel below it. A sentinel can
   * only sit a fixed distance down the page, which meant bringing the row into
   * comfortable view also tripped the loader, so the batch landed and the row
   * went spent before it could be pressed. Watching the row and requiring it to
   * have left upward (`top < 0`) encodes "the viewer has moved past these
   * options" exactly, with no distance to tune.
   *
   * Re-subscribed on `batches.length` so the ref always points at the row that
   * is currently live.
   */
  useEffect(() => {
    const liveRow = liveSteerRowRef.current;
    if (!liveRow) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry && !entry.isIntersecting && entry.boundingClientRect.top < 0) {
          loadBatch(undefined);
        }
      },
      { threshold: 0 }
    );
    observer.observe(liveRow);
    return () => observer.disconnect();
  }, [loadBatch, batches.length]);

  useEffect(() => {
    document.title = prototypeMeta.name;
  }, []);

  useEffect(() => {
    const updateTilesPerView = () => {
      const width = window.innerWidth;
      if (width >= 1440) {
        setCarouselTilesPerView(8);
      } else if (width >= 1280) {
        setCarouselTilesPerView(6);
      } else if (width >= 1024) {
        setCarouselTilesPerView(5);
      } else if (width >= 768) {
        setCarouselTilesPerView(4);
      } else {
        setCarouselTilesPerView(2);
      }
    };
    updateTilesPerView();
    window.addEventListener("resize", updateTilesPerView);
    return () => window.removeEventListener("resize", updateTilesPerView);
  }, []);

  const goToFindSimilar = () => history.push(FIND_SIMILAR_PATH);

  const commitSearch = (nextQuery: string) => {
    const trimmed = nextQuery.trim();
    if (trimmed.length === 0) {
      return;
    }
    setActiveQuery(trimmed);
    setTypeaheadQuery(trimmed);
    setIsTypeaheadOpen(false);
    setRecentQueries((current) =>
      [
        trimmed,
        ...current.filter(
          (entry) => entry.toLowerCase() !== trimmed.toLowerCase()
        ),
      ].slice(0, MAX_RECENT_QUERIES)
    );
  };

  /** Related searches sit at the bottom, so a new search returns to the top. */
  const runRelatedSearch = (query: string) => {
    commitSearch(query);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleFilterKey = (key: string) => {
    setAppliedFilterKeys((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleFilterCheckedValue = (value: string) => {
    setFilterCheckedValues((current) => {
      const next = new Set(current);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      return next;
    });
  };

  return (
    <CompassLayout>
      <LiveToolbar />
      <RetailerLayout
        navSearchBar={
          <TypeaheadSearchBar
            query={typeaheadQuery}
            onQueryChange={setTypeaheadQuery}
            onSubmitQuery={commitSearch}
            isOpen={isTypeaheadOpen}
            onOpenChange={setIsTypeaheadOpen}
            reorderedMatches={reorderedMatches}
            showReorderedSection={reorderedMatches.length > 0}
            suggestionRows={suggestionRows}
            suggestedSearches={RELATED_SEARCHES}
            recentQueries={recentQueries}
            now={now}
          />
        }
      >
        {/* Bottom padding clears the floating steer bar, so the last row of
            results can still be scrolled out from under it. */}
        <div
          className="mx-auto"
          style={{ maxWidth: "1920px", padding: "24px 48px 140px" }}
        >
          <div className="mb-8 -mr-12">
            <QuickFilterBar
              appliedKeys={appliedFilterKeys}
              onToggle={toggleFilterKey}
              onOpenAllFilters={() => setIsAllFiltersOpen(true)}
              extraFilters={queryRefinementFilters}
            />
          </div>

          <div className="mb-4">
            <SectionHeading>Top picks for you</SectionHeading>
          </div>
          <div className="mb-12 grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 lg:grid-cols-5">
            {topPicks.map((product) => (
              <ProductTileATC
                key={product.id}
                product={product}
                justified
                showFindSimilarOnImage
                onFindSimilarClick={goToFindSimilar}
                rationale={rationaleBulletsForProduct(product, activeQuery)}
              />
            ))}
          </div>

          <div className="mt-8 mb-4">
            <SectionHeading>All results</SectionHeading>
          </div>

          <ResultsGrid products={firstBand} onFindSimilar={goToFindSimilar} />

          <RelatedProductsCarousel
            title="Not stocked by nearby retailers"
            shopAllLink={{ href: "/my-brands", text: "Shop your brands" }}
            products={FIRST_CAROUSEL}
            tilesPerView={carouselTilesPerView}
          />

          <ResultsGrid products={secondBand} onFindSimilar={goToFindSimilar} />

          <RelatedProductsCarousel
            title="Trending products in your area"
            shopAllLink={{ href: "/category/Trending", text: "Shop trending" }}
            products={SECOND_CAROUSEL}
            tilesPerView={carouselTilesPerView}
          />

          <ResultsGrid products={thirdBand} onFindSimilar={goToFindSimilar} />

          <RelatedSearches
            searches={RELATED_SEARCHES}
            onSearchClick={runRelatedSearch}
          />

          {batches.length === 0 ? (
            <ShowMoreCta onClick={() => loadBatch(undefined, true)} />
          ) : (
            batches.map((batch, batchIndex) => (
              // Batches are append-only, so the index is a stable identity.
              <div key={batchIndex}>
                <div className="mt-8">
                  <ResultsGrid
                    products={batch.items}
                    onFindSimilar={goToFindSimilar}
                  />
                </div>
                {/* This row steers the batch *after* it, so once that batch
                    exists the row is spent: it shows what it produced and goes
                    inert, leaving only the last row live. */}
                <div
                  ref={
                    batchIndex === batches.length - 1
                      ? liveSteerRowRef
                      : undefined
                  }
                >
                  <ResultSteerRow
                    selected={batches[batchIndex + 1]?.steer}
                    spent={batchIndex < batches.length - 1}
                    onSelect={(steer) => loadBatch(steer, true)}
                  />
                </div>
              </div>
            ))
          )}

          {isLoadingMoreResults ? (
            <div className="mt-8">
              <ResultsGridSkeleton tileCount={SKELETON_TILE_COUNT} />
            </div>
          ) : null}

          {/* Scrolling this into view loads the next batch unsteered. Only
              mounted once the CTA has produced the first batch. */}
        </div>
      </RetailerLayout>

      <AllFiltersModal
        open={isAllFiltersOpen}
        onClose={() => setIsAllFiltersOpen(false)}
        size="large"
        heightVh={80}
        scrollShadows
        resultCount={visibleProducts.length}
        radioSelections={filterRadioSelections}
        onRadioChange={(sectionKey, value) =>
          setFilterRadioSelections((previous) => ({
            ...previous,
            [sectionKey]: value,
          }))
        }
        checkedValues={filterCheckedValues}
        onToggleChecked={toggleFilterCheckedValue}
        onClearAll={() => {
          setFilterRadioSelections({});
          setFilterCheckedValues(new Set());
        }}
      />
    </CompassLayout>
  );
}

/**
 * The one-time entry into the appended feed: related searches are one way
 * onward, this is the other. Once it produces the first batch the steer rows
 * and scroll take over, so it isn't rendered again.
 *
 * Slate's `Button` needs no styling help here — it is already rectangular at
 * `--slate-radius-component-default` (4px). The width cap lives on a wrapper
 * rather than the button, since Slate strips `className`.
 */
function ShowMoreCta({ onClick }: { onClick: () => void }) {
  return (
    <div className="pt-4 pb-2">
      <div className="mb-6 flex items-center justify-center gap-4">
        <span className="bg-fs-border-muted h-px w-full max-w-[100px] flex-1" />
        <span className="type-fs-paragraph text-fs-text-subdued">or</span>
        <span className="bg-fs-border-muted h-px w-full max-w-[100px] flex-1" />
      </div>
      <div className="mx-auto w-full max-w-[400px]">
        <Button fullWidth onClick={onClick}>
          Show more results
        </Button>
      </div>
    </div>
  );
}

interface ResultsGridProps {
  products: TileProduct[];
  onFindSimilar: () => void;
}

function ResultsGrid({ products: gridProducts, onFindSimilar }: ResultsGridProps) {
  return (
    <div className={resultsGridStyle.cls()}>
      {gridProducts.map((product, index) => (
        // Index-suffixed: batches loop back over the same finite product pool
        // (see `loadBatch`), so `product.id` alone repeats across pages.
        <ProductTileATCCompact
          key={`${product.id}-${index}`}
          product={product}
          onFindSimilarClick={onFindSimilar}
          useDefaultMetadataSize
        />
      ))}
    </div>
  );
}
